import { useEffect } from 'react';
import { Button, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import DownloadCSVButton from 'src/components/DownloadCSVButton';
import { filterProp } from 'src/interfaces/transactionsFilters';
import { useGetTransactionsMetadata } from 'src/hooks/transactions';
import { ECardType } from 'src/interfaces/transactions';

import { getNameCardType } from '../utils';

import {
  AMOUNT_BUCKETS,
  DescargaPanelFormValues,
  EMPTY_PANEL_VALUES,
  LOCAL_STATUS_OPTIONS,
  PROMO_OPTIONS,
  STATUS_LABELS,
  filterToPanelValues,
  panelValuesToFilter
} from './constants';
import { styles } from './styles';

interface Props {
  /** Filtro aplicado actual (para rehidratar el panel desde la URL). */
  appliedFilter: filterProp;
  /** Aplicar: única vía de impacto al listado (H4: on-submit only). */
  onApply: (filter: filterProp) => void;
  onDownloadCSV: () => Promise<string>;
  downloadFileName: string;
  isLoading?: boolean;
}

/**
 * Panel de filtros de la tab Descarga (H4): inline, siempre visible, grid de 5 columnas.
 * 8 filtros; opciones de Estado/Tipo/Banco/Bandera dinámicas desde /transactions/metadata
 * (con fallback local para Estado y estado de error sin romper la pantalla).
 * Los cambios NO afectan el listado hasta presionar Aplicar.
 */
const DescargaPanel = ({ appliedFilter, onApply, onDownloadCSV, downloadFileName, isLoading = false }: Props) => {
  const { classes, cx } = styles();
  const { data: metadataResult, isError: metadataError, isLoading: metadataLoading } = useGetTransactionsMetadata();
  const metadata = metadataResult?.data;

  const { control, handleSubmit, reset } = useForm<DescargaPanelFormValues>({
    defaultValues: filterToPanelValues(appliedFilter)
  });

  // Rehidratación: si el filtro aplicado cambia desde afuera (URL/montaje), reflejarlo.
  useEffect(() => {
    reset(filterToPanelValues(appliedFilter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilter]);

  const statusOptions = metadata?.statuses ?? LOCAL_STATUS_OPTIONS;
  const selectHelper = metadataError ? 'No pudimos cargar las opciones' : undefined;

  const submitApply = handleSubmit((values) => onApply(panelValuesToFilter(values)));

  const handleClear = () => {
    reset(EMPTY_PANEL_VALUES);
    onApply(panelValuesToFilter(EMPTY_PANEL_VALUES));
  };

  const renderSelect = (
    name: 'status' | 'cardType' | 'bank' | 'cardBrand',
    label: string,
    options: string[],
    getLabel: (value: string) => string = (v) => v
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <TextField
          {...field}
          select
          label={label}
          size="small"
          disabled={isLoading || metadataLoading || (metadataError && name !== 'status')}
          error={metadataError && name !== 'status'}
          helperText={name !== 'status' ? selectHelper : undefined}>
          <MenuItem value="">Todos</MenuItem>
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {getLabel(option)}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );

  return (
    <form aria-label="descarga-panel" onSubmit={submitApply} className={classes.panel}>
      <div className={classes.grid}>
        <Controller
          control={control}
          name="dateFrom"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              disableFuture
              label="Fecha desde"
              format="DD-MM-YYYY"
              value={value ? dayjs(value) : null}
              onChange={onChange}
              disabled={isLoading}
              slotProps={{ textField: { size: 'small' }, desktopPaper: { className: classes.pickerPaper } }}
            />
          )}
        />
        <Controller
          control={control}
          name="dateTo"
          render={({ field: { onChange, value } }) => (
            <DatePicker
              disableFuture
              label="Fecha hasta"
              format="DD-MM-YYYY"
              value={value ? dayjs(value) : null}
              onChange={onChange}
              disabled={isLoading}
              slotProps={{ textField: { size: 'small' }, desktopPaper: { className: classes.pickerPaper } }}
            />
          )}
        />
        {renderSelect('status', 'Estado', statusOptions, (v) => STATUS_LABELS[v] ?? v)}
        {/* Si el backend mandara un tipo que el portal no conoce, se muestra
            como viene: una opcion sin texto no se puede elegir. */}
        {renderSelect(
          'cardType',
          'Medio de pago',
          metadata?.card_types ?? [],
          (value) => getNameCardType(value as ECardType) || value
        )}
        {renderSelect('bank', 'Banco', metadata?.banks ?? [])}
        {renderSelect('cardBrand', 'Bandera', metadata?.card_brands ?? [])}
        <Controller
          control={control}
          name="promo"
          render={({ field }) => (
            <TextField {...field} select label="Promo" size="small" disabled={isLoading}>
              {PROMO_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="amountBucket"
          render={({ field }) => (
            <TextField {...field} select label="Monto" size="small" disabled={isLoading}>
              {AMOUNT_BUCKETS.map((bucket) => (
                <MenuItem key={bucket.key} value={bucket.key}>
                  {bucket.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>
      <div className={classes.actions}>
        <Button
          variant="text"
          className={cx(classes.actionButton, classes.clearButton)}
          onClick={handleClear}
          disabled={isLoading}>
          Limpiar
        </Button>
        <Button
          variant="outlined"
          className={cx(classes.actionButton, classes.applyButton)}
          type="submit"
          disabled={isLoading}>
          Aplicar
        </Button>
        <DownloadCSVButton handleDownload={onDownloadCSV} fileName={downloadFileName} disabled={isLoading} />
      </div>
    </form>
  );
};

export default DescargaPanel;
